/**
 * RemoteCatalogSource — `ICatalogSource` HTTP client for the public skaile.store
 * Catalog backend (see `store/backend`).
 *
 * Wire shape:
 *
 *   GET  /trpc/catalog.get?input=<superjson({ ref })>           → { result: { data: AssetDefinition | null } }
 *   GET  /trpc/catalog.list?input=<superjson({ filter? })>      → { result: { data: AssetDefinition[] } }
 *   GET  /tarball/<sha256>                                       → 307 → <storage url> (binary tarball)
 *
 * The tRPC server uses the standalone fastify adapter at `prefix: /trpc`
 * (`store/backend/libs/router-trpc/src/trpc.plugin.ts`). The transformer is
 * superjson, but our queries take only plain primitives + objects so the
 * `meta` envelope is empty in practice; we still send the canonical
 * `{ "json": ... }` shape so the server's superjson decoder is happy.
 *
 * The REST `GET /tarball/:sha256` controller lives at
 * `store/backend/apps/api/src/tarball.controller.ts` and 307-redirects to the
 * storage tier (presigned S3 URL or public CDN URL). The client is responsible
 * for following the redirect, downloading the bytes, and **verifying SHA256
 * client-side** — mismatch is a hard fail (spec invariant: "Hash pinning ...
 * Mismatch = hard fail").
 *
 * Cache: see {@link CatalogCache}. Stale-while-revalidate on metadata: a
 * stale entry serves the read while a background fetch refreshes the slot.
 * Tarballs are content-addressed and never expire — once cached on disk by
 * SHA256 they remain valid forever (until {@link CatalogCache.invalidateEverything}).
 *
 * Air-gapped: `cacheTtlMs: 0` disables network reads entirely. Every method
 * serves from cache or throws {@link OfflineError}; only `skaile update`
 * (which calls {@link RemoteCatalogSource.refresh}) flips the network on
 * for one bulk refresh.
 *
 * @docLink packages/library/api-reference#remote-catalog-source
 */

import { parseAssetRef } from "@skaile/workspaces/core";
import type {
  CatalogAsset,
  CatalogAssetFilter,
  CatalogDomain,
  CatalogDomainFilter,
  CatalogSourceInfo,
  DomainRelation,
  ICatalogSource,
} from "@skaile/workspaces/plugins";
import type { InstallManifest } from "@skaile/workspaces/types";

import { CatalogCache, type CatalogCacheOptions, sha256Hex } from "./cache.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A `fetch`-shaped function. Injected for tests; defaults to global `fetch`.
 *
 * @docLink packages/library/api-reference#remote-catalog-source
 */
export type HttpClient = (
  url: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    redirect?: "follow" | "manual" | "error";
  },
) => Promise<Response>;

/**
 * Constructor options for {@link RemoteCatalogSource}.
 *
 * @docLink packages/library/api-reference#remote-catalog-source
 */
export interface RemoteCatalogSourceOptions {
  /** Base URL of the Catalog backend, e.g. `https://api.skaile.store`. No trailing slash required. */
  baseUrl: string;
  /**
   * Metadata cache TTL in milliseconds.
   * Default `86_400_000` (24h). `0` disables network — air-gapped mode.
   */
  cacheTtlMs?: number;
  /** Override `fetch` for tests. */
  httpClient?: HttpClient;
  /** Override the cache directory. Default is `~/.skaile/cache/catalog/`. */
  cacheDir?: string;
  /** Override clock for tests. */
  now?: () => number;
  /** Pre-built cache instance (test injection). When provided, `cacheTtlMs`/`cacheDir`/`now` are ignored. */
  cache?: CatalogCache;
  /**
   * If true, suppresses the background revalidation `unhandledRejection` log when
   * a stale-while-revalidate fetch fails. Default false.
   */
  silentBackgroundErrors?: boolean;
}

/**
 * Thrown when the SHA256 of a downloaded tarball does not match the expected
 * value. This is a **hard fail** per the spec — the bytes are tampered or
 * corrupt and must not be installed.
 *
 * @docLink packages/library/api-reference#remote-catalog-source
 */
export class TarballHashMismatchError extends Error {
  constructor(
    public readonly expected: string,
    public readonly actual: string,
  ) {
    super(`SHA256 mismatch: expected ${expected}, got ${actual}`);
    this.name = "TarballHashMismatchError";
  }
}

/**
 * Thrown when the client is in air-gapped mode (`cacheTtlMs === 0`) and the
 * requested data is not in the cache. The caller can resolve by running
 * `skaile update` to perform a one-shot bulk refresh.
 *
 * @docLink packages/library/api-reference#remote-catalog-source
 */
export class OfflineError extends Error {
  constructor(message: string) {
    super(`${message} (air-gapped mode; run \`skaile update\` to refresh the cache)`);
    this.name = "OfflineError";
  }
}

/**
 * Thrown when the Catalog backend returns a non-2xx (and non-307 for tarball
 * fetch) response. Carries the HTTP status for debug.
 *
 * @docLink packages/library/api-reference#remote-catalog-source
 */
export class CatalogHttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly url?: string,
  ) {
    super(message);
    this.name = "CatalogHttpError";
  }
}

// ---------------------------------------------------------------------------
// RemoteCatalogSource
// ---------------------------------------------------------------------------

const DEFAULT_TTL_MS = 86_400_000; // 24h

/**
 * HTTP client implementation of `ICatalogSource` for the public skaile.store
 * Catalog. See module docstring for the wire contract.
 *
 * @docLink packages/library/api-reference#remote-catalog-source
 */
export class RemoteCatalogSource implements ICatalogSource {
  readonly id: string;
  readonly baseUrl: string;
  readonly cache: CatalogCache;
  private readonly cacheTtlMs: number;
  private readonly httpClient: HttpClient;
  private readonly silentBackgroundErrors: boolean;
  /** Tracks in-flight background revalidations so callers can `await` them in tests. */
  private readonly inFlight = new Map<string, Promise<void>>();

  constructor(opts: RemoteCatalogSourceOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.id = `remote:${this.baseUrl}`;
    this.cacheTtlMs = opts.cacheTtlMs ?? DEFAULT_TTL_MS;
    this.httpClient = opts.httpClient ?? defaultFetch;
    this.silentBackgroundErrors = opts.silentBackgroundErrors ?? false;
    this.cache =
      opts.cache ??
      new CatalogCache({
        dir: opts.cacheDir,
        ttlMs: this.cacheTtlMs,
        now: opts.now,
      } satisfies CatalogCacheOptions);
  }

  // ── ICatalogSource ──────────────────────────────────────────────────────

  /**
   * Resolve a single asset by canonical ref (`<publisher>/<name>@<version>`).
   *
   * Cache: hit returns immediately. Miss fetches `/trpc/catalog.get`. Stale
   * entry serves the cached value and triggers a background refresh.
   *
   * Air-gapped: throws {@link OfflineError} on cache miss.
   */
  async resolve(ref: string): Promise<CatalogAsset | null> {
    const fresh = this.cache.getResolve(ref);
    if (fresh) return fresh.value;

    if (this.cache.airGapped) {
      const stale = this.cache.getResolveStale(ref);
      if (stale) return stale.value;
      throw new OfflineError(`Asset '${ref}' not in cache`);
    }

    // Stale-while-revalidate: serve stale + refresh in background.
    const stale = this.cache.getResolveStale(ref);
    if (stale) {
      this.scheduleBackground(`resolve:${ref}`, () => this.fetchAndStoreResolve(ref));
      return stale.value;
    }

    return this.fetchAndStoreResolve(ref);
  }

  /**
   * List assets matching `filter`.
   *
   * Cache: hit returns immediately. Miss fetches `/trpc/catalog.list`. Stale
   * entry serves the cached array and triggers a background refresh.
   *
   * Air-gapped: throws {@link OfflineError} on cache miss.
   */
  async listAssets(filter?: CatalogAssetFilter): Promise<CatalogAsset[]> {
    const fresh = this.cache.getList(filter);
    if (fresh) return fresh.items;

    if (this.cache.airGapped) {
      const stale = this.cache.getListStale(filter);
      if (stale) return stale.items;
      throw new OfflineError("Catalog list not in cache");
    }

    const stale = this.cache.getListStale(filter);
    if (stale) {
      const filterKey = JSON.stringify(filter ?? null);
      this.scheduleBackground(`list:${filterKey}`, () => this.fetchAndStoreList(filter));
      return stale.items;
    }

    return this.fetchAndStoreList(filter);
  }

  /**
   * Fetch the content-addressed tarball for an asset. The bytes are downloaded
   * via the REST endpoint `GET /tarball/:sha256` (which 307-redirects to the
   * storage tier), then verified against `sha256` on the client side.
   *
   * Cache: tarballs are content-addressed and cached forever once written.
   *
   * Air-gapped: throws {@link OfflineError} on cache miss.
   *
   * @throws {@link TarballHashMismatchError} when the downloaded SHA256 does not match.
   */
  async fetchTarball(_ref: string, sha256: string): Promise<Uint8Array> {
    const cached = this.cache.readTarball(sha256);
    if (cached) {
      // Defensive: re-verify the cached blob. A bit-flip on disk should not
      // poison the install path. (Cheap — local file read, ~ms.)
      const actual = sha256Hex(cached);
      if (actual !== sha256) {
        throw new TarballHashMismatchError(sha256, actual);
      }
      return cached;
    }

    if (this.cache.airGapped) {
      throw new OfflineError(`Tarball '${sha256}' not in cache`);
    }

    const url = `${this.baseUrl}/tarball/${encodeURIComponent(sha256)}`;
    const res = await this.httpClient(url, { method: "GET", redirect: "follow" });
    if (!res.ok) {
      throw new CatalogHttpError(
        `Failed to fetch tarball ${sha256}: ${res.status} ${res.statusText}`,
        res.status,
        url,
      );
    }
    const buf = new Uint8Array(await res.arrayBuffer());
    const actual = sha256Hex(buf);
    if (actual !== sha256) {
      throw new TarballHashMismatchError(sha256, actual);
    }
    this.cache.writeTarball(sha256, buf);
    return buf;
  }

  /**
   * Enumerate available versions for a version-less ref. The Catalog has no
   * dedicated version-list route, so this derives versions from `listAssets`
   * (filtered to the publisher) and keeps only the matching kind+name. Reuses
   * the cached list path, so repeated calls are cheap.
   */
  async listVersions(ref: string): Promise<string[]> {
    const parsed = parseAssetRef(ref);
    // Stored publishers may or may not carry the `@` scope sigil — compare both
    // the wire filter and the in-memory match without it.
    const wantPublisher = parsed.publisher?.replace(/^@/, "");
    const assets = await this.listAssets(wantPublisher ? { publisher: wantPublisher } : undefined);
    return assets
      .filter(
        (a) =>
          a.kind === parsed.kind &&
          a.name === parsed.name &&
          (wantPublisher === undefined || a.publisher.replace(/^@/, "") === wantPublisher),
      )
      .map((a) => a.version);
  }

  /**
   * Single synthetic Source pointing at the configured Catalog URL. Phase 2
   * is single-Source per spec; multi-source federation is Phase 4.8.
   */
  async listSources(): Promise<CatalogSourceInfo[]> {
    return [{ id: this.id, type: "remote-catalog", url: this.baseUrl }];
  }

  /**
   * List navigational domains via `GET /trpc/catalog.listDomains`. Display-only,
   * so this **never throws**: a missing endpoint (older store → 404), a transport
   * error, or a malformed payload all degrade to `[]` rather than jeopardize the
   * asset feed this call accompanies. Air-gapped mode returns `[]` (domains are
   * not part of the offline cache in v1).
   *
   * Not cached — domains are read once per `skaile manage` open and are cheap.
   */
  async listDomains(filter?: CatalogDomainFilter): Promise<CatalogDomain[]> {
    if (this.cache.airGapped) return [];
    try {
      const input = filter && (filter.publisher || filter.source) ? filter : {};
      const url = trpcGetUrl(this.baseUrl, "catalog.listDomains", input);
      const res = await this.httpClient(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return [];
      const data = await parseTrpcGet(res);
      if (!Array.isArray(data)) return [];
      return data.map(normalizeCatalogDomain).filter((d): d is CatalogDomain => d !== null);
    } catch {
      return [];
    }
  }

  // ── extension API ───────────────────────────────────────────────────────

  /**
   * Force a metadata-cache refresh. Wipes all resolve/list entries and
   * re-fetches `listAssets()` (no filter) to pre-warm the canonical query.
   *
   * Driven by `skaile update`. Tarballs (content-addressed) are not touched
   * — they remain valid across refreshes.
   *
   * In air-gapped mode this method **does** make a network call (bypassing
   * the TTL=0 gate); it is the only way to repopulate the cache.
   */
  async refresh(): Promise<{ assetsCached: number }> {
    this.cache.invalidateAll();
    // Air-gapped bypass: call the network directly even when ttl is 0.
    const items = await this.fetchListNetwork(undefined);
    this.cache.setList(undefined, items);
    // Pre-warm resolve cache for known refs so subsequent reads are hot.
    for (const item of items) {
      this.cache.setResolve(item.id, item);
    }
    return { assetsCached: items.length };
  }

  /**
   * Fetch the pointer-only install manifest for an asset ref.
   *
   * The install manifest is the Pointer Triple: the immutable upstream
   * `source.url` + `source.commitSha`, the per-file `files[].sha256` list, a
   * composite `sha256` rollup, plus `kind` and `tier`. Callers fetch the asset
   * bytes directly from the upstream repo at that commit and verify each file
   * against the recorded hashes — the Catalog itself never serves asset bytes.
   *
   * Wire surface: `GET /trpc/catalog.getInstallManifest?input=<superjson({ ref })>`.
   *
   * This is a `RemoteCatalogSource`-only method — it is not part of the
   * `ICatalogSource` contract. `LocalCatalogSource` has the asset bytes on disk
   * and has no pointer-triple concept, so adding it to the shared interface
   * would force a meaningless implementation there.
   *
   * Not cached: the install path is a one-shot read driven by `skaile install`.
   *
   * @throws {@link CatalogHttpError} on a non-2xx response or malformed envelope.
   */
  async getInstallManifest(ref: string): Promise<InstallManifest> {
    const url = trpcGetUrl(this.baseUrl, "catalog.getInstallManifest", { ref });
    const res = await this.httpClient(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new CatalogHttpError(
        `catalog.getInstallManifest ${ref} failed: ${res.status} ${res.statusText}`,
        res.status,
        url,
      );
    }
    const data = await parseTrpcGet(res);
    if (data === null || data === undefined || typeof data !== "object") {
      throw new CatalogHttpError(
        `catalog.getInstallManifest ${ref} returned a non-object payload (got ${typeof data})`,
        200,
        url,
      );
    }
    return data as InstallManifest;
  }

  /**
   * Cheap probe used during cross-check when both a source-side and a store-side
   * candidate at the same version need to be verified for sha256 equality.
   * Returns only the composite sha256 — no source URL, no file list.
   *
   * Wire: `GET /trpc/catalog.getCanonicalDigest?input=<superjson({ ref })>`.
   *
   * Not cached: one-shot read during install resolution.
   *
   * @param ref - Canonical asset ref `<publisher>/<kind>:<name>@<version>`.
   * @returns `{ sha256 }` on a 200, or `null` on a 404.
   * @throws {@link CatalogHttpError} on a non-2xx/non-404 response or malformed payload.
   * @docLink packages/library/api-reference#remote-catalog-source
   */
  async getCanonicalDigest(ref: string): Promise<{ sha256: string } | null> {
    const url = trpcGetUrl(this.baseUrl, "catalog.getCanonicalDigest", { ref });
    const res = await this.httpClient(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (res.status === 404) return null;
    if (!res.ok) {
      throw new CatalogHttpError(
        `catalog.getCanonicalDigest ${ref} failed: ${res.status} ${res.statusText}`,
        res.status,
        url,
      );
    }
    const data = await parseTrpcGet(res);
    if (!data || typeof data !== "object") {
      throw new CatalogHttpError(
        `catalog.getCanonicalDigest ${ref} returned a non-object payload`,
        200,
        url,
      );
    }
    const d = data as { sha256?: unknown };
    if (typeof d.sha256 !== "string") {
      throw new CatalogHttpError(
        `catalog.getCanonicalDigest ${ref} missing sha256 string`,
        200,
        url,
      );
    }
    return { sha256: d.sha256 };
  }

  /**
   * Wait for any in-flight stale-while-revalidate background fetches to settle.
   * Test-only escape hatch.
   */
  async waitForBackground(): Promise<void> {
    // Snapshot to avoid mutation during iteration.
    const promises = Array.from(this.inFlight.values());
    await Promise.allSettled(promises);
  }

  // ── private ─────────────────────────────────────────────────────────────

  private scheduleBackground(key: string, fn: () => Promise<unknown>): void {
    if (this.inFlight.has(key)) return;
    const p = (async () => {
      try {
        await fn();
      } catch (err) {
        if (!this.silentBackgroundErrors) {
          process.stderr.write(
            `[skaile/catalog] Background refresh failed for ${key}: ${err instanceof Error ? err.message : String(err)}\n`,
          );
        }
      } finally {
        this.inFlight.delete(key);
      }
    })();
    this.inFlight.set(key, p);
  }

  private async fetchAndStoreResolve(ref: string): Promise<CatalogAsset | null> {
    const value = await this.fetchResolveNetwork(ref);
    this.cache.setResolve(ref, value);
    return value;
  }

  private async fetchAndStoreList(filter?: CatalogAssetFilter): Promise<CatalogAsset[]> {
    const items = await this.fetchListNetwork(filter);
    this.cache.setList(filter, items);
    return items;
  }

  /** Raw network call: GET /trpc/catalog.get?input=<superjson> */
  private async fetchResolveNetwork(ref: string): Promise<CatalogAsset | null> {
    const url = trpcGetUrl(this.baseUrl, "catalog.get", { ref });
    const res = await this.httpClient(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new CatalogHttpError(
        `catalog.get ${ref} failed: ${res.status} ${res.statusText}`,
        res.status,
        url,
      );
    }
    const data = await parseTrpcGet(res);
    if (data === null || data === undefined) return null;
    return normalizeCatalogAsset(data);
  }

  /** Raw network call: GET /trpc/catalog.list?input=<superjson> */
  private async fetchListNetwork(filter?: CatalogAssetFilter): Promise<CatalogAsset[]> {
    const input = filter ? { filter } : {};
    const url = trpcGetUrl(this.baseUrl, "catalog.list", input);
    const res = await this.httpClient(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new CatalogHttpError(
        `catalog.list failed: ${res.status} ${res.statusText}`,
        res.status,
        url,
      );
    }
    const data = await parseTrpcGet(res);
    if (!Array.isArray(data)) {
      throw new CatalogHttpError(
        `catalog.list returned non-array payload (got ${typeof data})`,
        200,
        url,
      );
    }
    return data.map(normalizeCatalogAsset);
  }
}

// ---------------------------------------------------------------------------
// tRPC GET helpers
// ---------------------------------------------------------------------------

/**
 * Build a tRPC v11 GET URL with superjson-encoded input.
 *
 * Format: `<base>/trpc/<procedure>?input=<urlencoded({"json": <input>})>`
 *
 * superjson encodes plain values as `{ "json": <value> }`. The `meta` block is
 * only present for non-JSON-native types (Date, Map, BigInt, etc.) — our inputs
 * are plain strings/objects so we never emit `meta`.
 *
 * Exported for tests.
 *
 * @docLink packages/library/api-reference#remote-catalog-source
 */
export function trpcGetUrl(baseUrl: string, procedure: string, input: unknown): string {
  const wrapped = JSON.stringify({ json: input });
  return `${baseUrl.replace(/\/+$/, "")}/trpc/${procedure}?input=${encodeURIComponent(wrapped)}`;
}

/**
 * Parse a tRPC GET response body and unwrap the superjson `result.data`
 * envelope. Throws {@link CatalogHttpError} when the response shape is
 * malformed or carries a tRPC `error` payload.
 */
async function parseTrpcGet(res: Response): Promise<unknown> {
  const text = await res.text();
  let body: unknown;
  try {
    body = text.length > 0 ? JSON.parse(text) : null;
  } catch {
    throw new CatalogHttpError(
      `tRPC response is not valid JSON: ${text.slice(0, 200)}`,
      res.status,
    );
  }

  if (body && typeof body === "object" && "error" in body) {
    const err = (body as { error: { message?: string; data?: { code?: string } } }).error;
    throw new CatalogHttpError(
      `tRPC error: ${err.message ?? "unknown"}${err.data?.code ? ` (${err.data.code})` : ""}`,
      res.status,
    );
  }

  // tRPC v11 GET shape: { result: { data: <superjson-encoded> } }
  // superjson value shape: { json: <value>, meta?: {...} }
  const result = (body as { result?: { data?: unknown } } | null)?.result;
  if (!result || !("data" in result)) {
    throw new CatalogHttpError(`tRPC response missing result.data envelope`, res.status);
  }
  const data = result.data as unknown;
  // superjson encoding: pull out the `json` key if it's the wrapped shape.
  if (data && typeof data === "object" && !Array.isArray(data) && "json" in (data as object)) {
    return (data as { json: unknown }).json;
  }
  return data;
}

// ---------------------------------------------------------------------------
// Server → CatalogAsset normalization
// ---------------------------------------------------------------------------

/**
 * The store backend `AssetDefinition` row shape (post-PostXL) carries fields
 * that don't map 1:1 onto {@link CatalogAsset}. This helper produces a stable
 * client-side shape: it computes the canonical ref string, falls back to the
 * generated ID when the server already provides a canonical ref, and copies
 * across optional metadata.
 */
function normalizeCatalogAsset(raw: unknown): CatalogAsset {
  if (!raw || typeof raw !== "object") {
    throw new CatalogHttpError("Catalog response item is not an object", 200);
  }
  const r = raw as Record<string, unknown>;
  const publisher = pickString(r, "publisher") ?? pickString(r, "publisherSlug") ?? "";
  const name = pickString(r, "name") ?? "";
  const version = pickString(r, "version") ?? "";
  const id = pickString(r, "id") ?? `${publisher}/${name}@${version}`;
  const kind = pickString(r, "kind") ?? "";
  const manifest =
    typeof r.manifest === "object" && r.manifest !== null
      ? (r.manifest as Record<string, unknown>)
      : typeof r.manifestJson === "object" && r.manifestJson !== null
        ? (r.manifestJson as Record<string, unknown>)
        : {};

  const asset: CatalogAsset = {
    id,
    kind,
    publisher,
    name,
    version,
    manifest,
  };
  const sha = pickString(r, "sha256") ?? pickString(r, "tarballSha256");
  if (sha) asset.sha256 = sha;
  const description = pickString(r, "description");
  if (description) asset.description = description;
  const license = pickString(r, "license");
  if (license) asset.license = license;
  const category = pickString(r, "category");
  if (category) asset.category = category;
  const repo = pickString(r, "repo");
  if (repo) asset.repo = repo;
  const domainSlug = pickString(r, "domainSlug");
  if (domainSlug) asset.domainSlug = domainSlug;
  const softDeps = pickRefs(r, "softDepsJson");
  if (softDeps.length > 0) asset.softDeps = softDeps;
  return asset;
}

/** Extract soft-dep refs from a catalog field shaped `{ refs: string[] }` or a bare `string[]`. */
function pickRefs(r: Record<string, unknown>, key: string): string[] {
  const v = r[key];
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === "string");
  if (v && typeof v === "object" && Array.isArray((v as { refs?: unknown }).refs)) {
    return (v as { refs: unknown[] }).refs.filter((x): x is string => typeof x === "string");
  }
  return [];
}

function pickString(r: Record<string, unknown>, key: string): string | undefined {
  const v = r[key];
  return typeof v === "string" ? v : undefined;
}

/**
 * Map a `catalog.listDomains` wire row → {@link CatalogDomain}. Returns `null`
 * for a row missing the load-bearing `slug`/`title` (skipped by the caller) so a
 * single malformed entry never poisons the whole list. Display-only fields are
 * copied through when present; `relation` is narrowed to the known union.
 */
function normalizeCatalogDomain(raw: unknown): CatalogDomain | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const slug = pickString(r, "slug");
  const title = pickString(r, "title");
  if (!slug || !title) return null;

  const domain: CatalogDomain = { slug, title };
  const description = pickString(r, "description");
  if (description) domain.description = description;
  const relation = pickString(r, "relation");
  if (relation === "additions" || relation === "alternatives" || relation === "sequence") {
    domain.relation = relation satisfies DomainRelation;
  }
  if (typeof r.order === "number") domain.order = r.order;
  const recommendedRef = pickString(r, "recommendedRef");
  if (recommendedRef) domain.recommendedRef = recommendedRef;
  const sidecarSource = pickString(r, "sidecarSource");
  if (sidecarSource) domain.sidecarSource = sidecarSource;
  return domain;
}

// ---------------------------------------------------------------------------
// Default fetch
// ---------------------------------------------------------------------------

/**
 * Upper bound on a single catalog HTTP request. Without it, a hung or
 * unreachable backend would block `resolve()` / `listAssets()` indefinitely
 * (a dropped connection never yields a TCP RST) and stall the caller.
 */
const CATALOG_REQUEST_TIMEOUT_MS = 10_000;

const defaultFetch: HttpClient = async (url, init) => {
  if (typeof fetch === "undefined") {
    throw new Error("global fetch is not available; pass an httpClient explicitly");
  }
  const requestInit = (init ?? {}) as RequestInit;
  return fetch(url, {
    ...requestInit,
    signal: requestInit.signal ?? AbortSignal.timeout(CATALOG_REQUEST_TIMEOUT_MS),
  });
};
